/**
 * VAU context-switch HTTP client.
 *
 * Talks to the backend's ``/backend/api/v1/vau`` endpoints — assume,
 * release, and the assumed-context probe used to populate the left-nav
 * switcher and the "Acting as …" banner.
 */

import http from '@frontend/api/http';
import { OPAL_BACKEND_URL } from '@frontend/envConfig';

import type { AssumeResponse, ReleaseResponse, VAUContextResponse } from '../types/vauContext';

const base = `${OPAL_BACKEND_URL}/backend/api/v1/vau`;

// CSRF defense header — the backend's assume/release endpoints require
// `X-Requested-With: XMLHttpRequest`. Browsers do not allow cross-origin
// form submissions to set custom headers without a CORS preflight, so a
// malicious site cannot forge an assume/release call against the victim's
// cookie-authenticated session.
const CSRF_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' } as const;

export const getAssumedContext = async (): Promise<VAUContextResponse> => {
  const res = await http.get<VAUContextResponse>(`${base}/assumed-context`);
  return res.data;
};

export const assumeVAU = async (vauId: string): Promise<AssumeResponse> => {
  // Cold-path token-service `/vau-tokens/{vau_id}` does an identity-api →
  // Okta round-trip to mint the VAU OBO. Measured at ~14s on cache miss
  // (vs ~85ms once cached). The global axios timeout is 10s — correct for
  // every other API call, but it aborts assume before the backend
  // completes the cold OBO mint. Override only this call, not the global
  // default, so other mutations stay tight on the 10s ceiling.
  const res = await http.post<AssumeResponse>(`${base}/${vauId}/assume`, undefined, {
    timeout: 30000,
    headers: CSRF_HEADERS,
  });
  return res.data;
};

export const releaseVAU = async (): Promise<ReleaseResponse> => {
  const res = await http.post<ReleaseResponse>(`${base}/release`, undefined, {
    headers: CSRF_HEADERS,
  });
  return res.data;
};
